import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useViewerItemStore, ItemSharingMode } from '@/modules/viewer/store/useViewerItemStore';

import { useSecurity } from '@/modules/viewer/composables/useSecurity';

import InviteModal from '@/components/InviteModal.vue';
import { TrackClickEvents } from '@/composables/useTrack';

export const useSecurityToolbar = () => {
  const organizationStore = useOrganizationStore();
  const viewerItemStore = useViewerItemStore();

  const { changeSharingMode } = useSecurity();

  const { free } = storeToRefs(organizationStore);
  const { sharing } = storeToRefs(viewerItemStore);
  //
  // MENU ITEMS
  //
  const viewGroups = [
    {
      title: 'Anyone with the link can view',
      icon: 'general.globe',
      name: ItemSharingMode.UNRESTRICTED,
      trackClick: TrackClickEvents.ITEM_SECURITY_LINK,
      action: () => changeSharingMode(ItemSharingMode.UNRESTRICTED),
    },
    {
      title: 'In your organization',
      icon: 'general.organization',
      name: ItemSharingMode.ORGANIZATION_VIEW,
      disabled: free?.value,
      trackClick: TrackClickEvents.ITEM_SECURITY_ORG_VIEW,
      action: () => changeSharingMode(ItemSharingMode.ORGANIZATION_VIEW),
    },
    {
      title: 'Only emailed people',
      icon: 'general.users',
      name: ItemSharingMode.SPECIFIC_USER_VIEW,
      trackClick: TrackClickEvents.ITEM_SECURITY_EMAILED_PPL,
      disabled: free?.value,
      action: () => changeSharingMode(ItemSharingMode.SPECIFIC_USER_VIEW),
    },
  ];

  const userAccess = [
    {
      title: 'Open access',
      icon: 'general.unlockKeyAlt',
      name: ItemSharingMode.UNRESTRICTED,
      action: () => changeSharingMode(ItemSharingMode.UNRESTRICTED),
    },
    {
      title: 'Require password',
      icon: 'general.shieldKey',
      name: ItemSharingMode.PASSWORD,
      trackClick: TrackClickEvents.ITEM_SECURITY_PASSWORD,
      disabled: free?.value,
      action: () => changeSharingMode(ItemSharingMode.PASSWORD),
    },
    {
      title: 'Require email',
      subtitle: 'Viewers must enter an email to view',
      icon: 'general.shieldEmail',
      name: ItemSharingMode.EMAIL,
      trackClick: TrackClickEvents.ITEM_SECURITY_EMAIL,
      disabled: free?.value,
      action: () => changeSharingMode(ItemSharingMode.EMAIL),
    },
  ];

  const orgAccess = [
    {
      title: 'Can view',
      icon: 'general.eye',
      name: ItemSharingMode.ORGANIZATION_VIEW,
      disabled: free?.value,
      action: () => changeSharingMode(ItemSharingMode.ORGANIZATION_VIEW),
    },
    {
      title: 'Can edit',
      icon: 'general.pencil',
      name: ItemSharingMode.ORGANIZATION_EDIT,
      disabled: free?.value,
      action: () => changeSharingMode(ItemSharingMode.ORGANIZATION_EDIT),
    },
    { isSeparator: true },
    {
      title: 'Invite team members',
      icon: 'general.inviteToTeam',
      name: 'invite-team-members',
      disabled: free?.value,
      action: {
        type: 'modal',
        params: {
          component: InviteModal,
        },
      },
    },
  ];

  /**
   * Setting is grouped for 2 menus this grouping is for 1st menu "active" item.
   * @param sharing The sharing mode of the item.
   * @returns Default share mode for specific group or undefined if the sharing mode is not recognized.
   */
  const getWhoIsViewing = (sharing: ItemSharingMode | undefined) => {
    switch (sharing) {
      case ItemSharingMode.UNRESTRICTED:
      case ItemSharingMode.PASSWORD:
      case ItemSharingMode.EMAIL:
        return viewGroups.find(item => item.name === ItemSharingMode.UNRESTRICTED);

      case ItemSharingMode.ORGANIZATION_VIEW:
      case ItemSharingMode.ORGANIZATION_EDIT:
        return viewGroups.find(item => item.name === ItemSharingMode.ORGANIZATION_VIEW);

      case ItemSharingMode.SPECIFIC_USER_VIEW:
      case ItemSharingMode.SPECIFIC_USER_EDIT:
        return viewGroups.find(item => item.name === ItemSharingMode.SPECIFIC_USER_VIEW);

      default:
        return undefined;
    }
  };

  /**
   * List of users or organization specific sharing modes.
   * @param sharing The sharing mode of the item.
   * @returns Array of user or organization specific modes or undefined if the sharing mode is not recognized.
   */
  const getWhoHasAccess = (sharing: ItemSharingMode | undefined): any[] | undefined => {
    switch (sharing) {
      case ItemSharingMode.UNRESTRICTED:
      case ItemSharingMode.PASSWORD:
      case ItemSharingMode.EMAIL:
        return userAccess;

      case ItemSharingMode.ORGANIZATION_VIEW:
      case ItemSharingMode.ORGANIZATION_EDIT:
        return orgAccess;

      default:
        return undefined;
    }
  };

  /**
   * Setting is grouped for 2 menus this grouping is for 2nd menu "active" item.
   * @param sharing The sharing mode of the item.
   * @returns User or group share mode or undefined if the sharing mode is not recognized.
   */
  const getWhoIsAccessing = (sharing: ItemSharingMode | undefined) => {
    const whoHasAccess = getWhoHasAccess(sharing);
    if (!whoHasAccess) {
      return undefined;
    }

    return whoHasAccess.find((item: any) => item.name && item.name === sharing);
  };

  const whoCanView = computed(() => viewGroups);
  const whoIsViewing = computed(() => getWhoIsViewing(sharing.value));
  const whoHasAccess = computed(() => getWhoHasAccess(sharing.value));
  const whoIsAccessing = computed(() => getWhoIsAccessing(sharing.value));

  return { whoCanView, whoIsViewing, whoHasAccess, whoIsAccessing };
};
